import React, { useState, useMemo } from "react";
import RoleToggle from "../../components/RoleToggle";
import NewinputField from "../../components/NewInput";
import NewSelectInput from "../../components/NewSelectInput";
import { useLanguage } from "../../context/LanguageContext";
import { useProfileData } from "../../apis/profileApis";
import {
  useMerchantAllowedShippingChannels,
  useConnectMerchant3PLCredentialsMutation,
  useValidateTrendyolCredentialsMutation,
  useConnectTrendyolMerchantMutation,
  useDisconnectTrendyolMerchantMutation,
} from "../../apis/integrationApis";
import { useWarehouseListData } from "../../apis/warehouseApi";
import { useFetchServicesData } from "../../apis/servicesApis";
import toast from "react-hot-toast";
import {
  IconCheck,
  IconSearch,
  IconX,
  IconTruckDelivery,
  IconFilter,
  IconBolt,
  IconAlertCircle,
  IconBuildingWarehouse,
  IconTrash,
  IconKey,
} from "@tabler/icons-react";

type CredentialField = {
  key: string;
  label: string;
  placeholder?: string;
  required: boolean;
  type?: "text" | "password" | "select";
  options?: { label: string; value: string }[];
};

const partnerFields: Record<string, CredentialField[]> = {
  Aramex: [
    { key: "AccountNumber", label: "Account Number", required: true, placeholder: "Enter Account Number" },
    { key: "AccountPin", label: "Account Pin", required: true, placeholder: "Enter Account Pin" },
    { key: "UserName", label: "Username", required: true, placeholder: "Enter Username" },
    { key: "Password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "AccountEntity", label: "Account Entity", required: true, placeholder: "Enter Account Entity" },
    { key: "AccountCountryCode", label: "Account Country Code", required: true, placeholder: "Enter Account Country Code" },
    {
      key: "PaymentType",
      label: "Payment Type",
      required: true,
      type: "select",
      placeholder: "Select Payment Type",
      options: [
        { label: "P (Prepaid)", value: "P" },
        { label: "C (Credit)", value: "C" },
        { label: "3 (Third Party)", value: "3" },
      ],
    },
    {
      key: "ProductGroup",
      label: "Product Group",
      required: true,
      type: "select",
      placeholder: "Select Product Group",
      options: [
        { label: "DOM (Domestic)", value: "DOM" },
        { label: "EXP (Express / International)", value: "EXP" },
      ],
    },
    {
      key: "ProductType",
      label: "Product Type",
      required: true,
      type: "select",
      placeholder: "Select Product Type",
      options: [
        { label: "CDS (Courier Domestic Service)", value: "CDS" },
        { label: "PDX (Priority Parcel Express)", value: "PDX" },
        { label: "PPX (Prepaid Parcel Express)", value: "PPX" },
        { label: "ONP (Overnight Parcel)", value: "ONP" },
        { label: "EPX (Economy Parcel Express)", value: "EPX" },
        { label: "DLV (Delivery)", value: "DLV" },
      ],
    },
  ],
  SMSA: [
    { key: "username", label: "Username", required: true, placeholder: "Enter SMSA username" },
    { key: "password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
  ],
  Naqel: [
    { key: "userName", label: "Username", required: true, placeholder: "Enter Naqel username" },
    { key: "password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "customerCode", label: "Customer Code", required: true, placeholder: "e.g. NL123456" },
    { key: "branchCode", label: "Branch Code", required: true, placeholder: "e.g. NL567899" },
  ],
  "Naqel Express": [
    { key: "userName", label: "Username", required: true, placeholder: "Enter Naqel username" },
    { key: "password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "customerCode", label: "Customer Code", required: true, placeholder: "e.g. NL122212" },
    { key: "branchCode", label: "Branch Code", required: true, placeholder: "e.g. NL312542" },
  ],
  "J&T Express": [
    { key: "customerCode", label: "Customer Code", required: true, placeholder: "Enter customer code" },
    { key: "apiAccount", label: "API Account", required: true, placeholder: "Enter API account" },
    { key: "privateKey", label: "Private Key", required: true, type: "password", placeholder: "Leave blank to keep existing" },
  ],
  Zid: [
    { key: "clientId", label: "Client ID", required: true, placeholder: "e.g. 1234" },
    { key: "clientSecret", label: "Client Secret", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "appId", label: "App ID", required: true, placeholder: "e.g. 1234" },
  ],
  Shopify: [
    { key: "shopDomain", label: "Shop Domain", required: true, placeholder: "e.g. myshop.myshopify.com" },
    { key: "appId", label: "API Key", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "secretKey", label: "API Secret Key", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "accessToken", label: "Admin Access Token", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "apiVersion", label: "API Version", required: true, placeholder: "e.g. 2024-07" },
  ],
  Shoapify: [
    { key: "shopDomain", label: "Shop Domain", required: true, placeholder: "e.g. myshop.myshopify.com" },
    { key: "appId", label: "API Key", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "secretKey", label: "API Secret Key", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "accessToken", label: "Admin Access Token", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "apiVersion", label: "API Version", required: true, placeholder: "e.g. 2024-07" },
  ],
  Trendyol: [
    { key: "sellerId", label: "Seller ID", required: true, placeholder: "e.g. 123456" },
    { key: "apiKey", label: "API Key", required: true, type: "password", placeholder: "Enter Trendyol API Key" },
    { key: "apiSecret", label: "API Secret", required: true, type: "password", placeholder: "Enter Trendyol API Secret" },
    {
      key: "storefrontCode",
      label: "Storefront / Country",
      required: true,
      type: "select",
      placeholder: "Select Storefront Country",
    },
    { key: "warehouseId", label: "Fulfillment Warehouse", required: false, type: "select", placeholder: "Select Warehouse" },
    { key: "serviceId", label: "Default Service", required: false, type: "select", placeholder: "Select Service" },
  ],
};

const STOREFRONT_OPTIONS = [
  { id: "SA", value: "SA", text: "🇸🇦 SA — Saudi Arabia" },
  { id: "TR", value: "TR", text: "🇹🇷 TR — Turkey" },
  { id: "AE", value: "AE", text: "🇦🇪 AE — United Arab Emirates" },
  { id: "DE", value: "DE", text: "🇩🇪 DE — Germany" },
  { id: "GR", value: "GR", text: "🇬🇷 GR — Greece" },
  { id: "SK", value: "SK", text: "🇸🇰 SK — Slovakia" },
  { id: "RO", value: "RO", text: "🇷🇴 RO — Romania" },
  { id: "CZ", value: "CZ", text: "🇨🇿 CZ — Czechia" },
  { id: "BG", value: "BG", text: "🇧🇬 BG — Bulgaria" },
  { id: "KW", value: "KW", text: "🇰🇼 KW — Kuwait" },
];

const SectionHeader = ({
  icon: Icon,
  title,
  rightContent,
}: {
  icon?: any;
  title: string;
  rightContent?: React.ReactNode;
}) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center justify-between border-b border-[#FBD037]/30 pb-1.5 mb-2.5">
      <div className="flex items-center gap-2">
        {Icon && (
          <div className="p-1 rounded bg-[#FBD037]/20 text-[#212121]">
            <Icon size={14} stroke={1.8} />
          </div>
        )}
        <p className="text-[#212121]/90 uppercase text-[11px] font-bold tracking-wider">
          {t(title)}
        </p>
      </div>
      {rightContent}
    </div>
  );
};

const Integration: React.FC = () => {
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState("All Channels");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const { data: profile } = useProfileData();
  const clientId = profile?.id || profile?.clientId || "";

  const { data: allowedChannels, isLoading, refetch } = useMerchantAllowedShippingChannels(clientId);
  const connectMutation = useConnectMerchant3PLCredentialsMutation();
  const validateTrendyolMutation = useValidateTrendyolCredentialsMutation();
  const connectTrendyolMutation = useConnectTrendyolMerchantMutation();
  const disconnectTrendyolMutation = useDisconnectTrendyolMerchantMutation();

  const { data: warehouseRes } = useWarehouseListData();
  const { data: servicesRes } = useFetchServicesData(1, 50, "");

  const warehouses = useMemo(() => {
    return Array.isArray(warehouseRes)
      ? warehouseRes
      : warehouseRes?.warehouses || warehouseRes?.data || [];
  }, [warehouseRes]);

  const services = useMemo(() => {
    return Array.isArray(servicesRes)
      ? servicesRes
      : servicesRes?.services || servicesRes?.data || [];
  }, [servicesRes]);

  const warehouseOptions = useMemo(
    () => [
      { id: "", value: "", text: t("Select Warehouse (Optional)") },
      ...warehouses.map((w: any) => ({
        id: w.id,
        value: w.id,
        text: `${w.name} ${w.location ? `(${w.location})` : ""}`,
      })),
    ],
    [warehouses, t]
  );

  const serviceOptions = useMemo(
    () => [
      { id: "", value: "", text: t("Select Service (Optional)") },
      ...(services.length > 0
        ? services
        : [
          { id: "c390a39e-a2d7-4d71-8204-18e1c0e27f97", name: "Standard Delivery" },
          { id: "fcdc205b-9569-42d6-8e25-0d91bb028f4d", name: "Express / Priority Delivery" },
        ]
      ).map((s: any) => ({
        id: s.id,
        value: s.id,
        text: s.name,
      })),
    ],
    [services, t]
  );

  const [selectedChannel, setSelectedChannel] = useState<any | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [environment, setEnvironment] = useState<"test" | "prod">("prod");
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleOpenConnect = (channel: any) => {
    setSelectedChannel(channel);
    setTestResult(null);
    setEnvironment(channel.environment || "prod");
    const companyName = channel.companyName || channel.title;
    const fields = partnerFields[companyName] || channel.credentialsFields || channel.credentialKeys || [];

    const existingCreds = channel.credentials || {};
    const initialData: Record<string, string> = {};

    fields.forEach((f: any) => {
      const key = typeof f === "string" ? f : f.key;
      initialData[key] = existingCreds[key] || "";
    });

    if (companyName?.toLowerCase() === "trendyol") {
      initialData.sellerId = existingCreds.sellerId || channel.sellerId || "";
      initialData.apiKey = existingCreds.apiKey || "";
      initialData.apiSecret = existingCreds.apiSecret || "";
      initialData.storefrontCode = existingCreds.storefrontCode || existingCreds.storeFrontCode || "SA";
      initialData.warehouseId = existingCreds.warehouseId || channel.defaultWarehouseId || "";
      initialData.serviceId = existingCreds.serviceId || channel.defaultServiceId || "";
    }

    setFormData(initialData);
  };

  const handleTestConnection = async () => {
    setTestResult(null);
    const sellerId = formData.sellerId?.trim() || "";
    const apiKey = formData.apiKey?.trim() || "";
    const apiSecret = formData.apiSecret?.trim() || "";
    const storeFrontCode = formData.storefrontCode || "SA";

    if (!sellerId) {
      toast.error(t("Seller ID is required"));
      return;
    }
    if (!apiKey) {
      toast.error(t("API Key is required"));
      return;
    }
    if (!apiSecret) {
      toast.error(t("API Secret is required"));
      return;
    }

    try {
      const res = await validateTrendyolMutation.mutateAsync({
        sellerId,
        apiKey,
        apiSecret,
        storeFrontCode,
      });
      setTestResult({
        success: true,
        message: res.message || t("Trendyol credentials verified successfully!"),
      });
      toast.success(t("Trendyol connection verified!"));
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err?.message || t("Validation failed with Trendyol API"),
      });
      toast.error(err?.message || t("Validation failed"));
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t("Are you sure you want to disconnect your Trendyol store?"))) return;
    try {
      await disconnectTrendyolMutation.mutateAsync({ merchantId: clientId });
      toast.success(t("Trendyol store disconnected"));
      setSelectedChannel(null);
      refetch();
    } catch (err: any) {
      toast.error(err?.message || t("Failed to disconnect"));
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedChannel) return;

    const companyName = selectedChannel.companyName || selectedChannel.title;
    const isTrendyol = companyName?.toLowerCase() === "trendyol";

    if (isTrendyol) {
      const isConnected = selectedChannel.status === "Connected" || selectedChannel.isConnected;
      if (!formData.sellerId?.trim()) {
        toast.error(t("Please enter your Trendyol Seller ID."));
        return;
      }
      if (!isConnected && (!formData.apiKey?.trim() || !formData.apiSecret?.trim())) {
        toast.error(t("Please fill Seller ID, API Key, and API Secret."));
        return;
      }

      connectTrendyolMutation.mutate(
        {
          merchantId: clientId,
          sellerId: formData.sellerId.trim(),
          apiKey: formData.apiKey ? formData.apiKey.trim() : "",
          apiSecret: formData.apiSecret ? formData.apiSecret.trim() : "",
          storefrontCode: formData.storefrontCode || "SA",
          warehouseId: formData.warehouseId || undefined,
          serviceId: formData.serviceId || undefined,
        },
        {
          onSuccess: () => {
            toast.success(t("Trendyol store connected successfully!"));
            setSelectedChannel(null);
            refetch();
          },
          onError: (err: any) => {
            toast.error(err?.message || t("Failed to connect Trendyol store"));
          },
        }
      );
      return;
    }

    const connectionId = selectedChannel.connectionId || selectedChannel.id;

    connectMutation.mutate(
      {
        connectionId,
        credentials: {
          ...formData,
          clientId,
          channelId: selectedChannel.id,
        },
        environment,
      },
      {
        onSuccess: () => {
          toast.success(
            `${selectedChannel.companyName || selectedChannel.title} credentials saved successfully!`
          );
          setSelectedChannel(null);
          refetch();
        },
        onError: (err: any) => {
          toast.error(err?.message || t("Failed to save credentials"));
        },
      }
    );
  };

  const channelsList = Array.isArray(allowedChannels) ? allowedChannels : [];

  const filteredChannels = channelsList.filter((ch: any) => {
    const name = (ch.companyName || ch.title || "").toLowerCase();
    const matchesSearch = name.includes(searchTerm.toLowerCase());
    const chType = (ch.type || ch.channelType || "").toLowerCase();

    const matchesType =
      activeSection === "All Channels" ||
      (activeSection === "Shipping" && (chType === "shipping" || !chType)) ||
      (activeSection === "Sales" && (chType === "sales" || name.includes("trendyol") || name.includes("shopify") || name.includes("zid")));

    const isConnected = ch.status === "Connected" || ch.isConnected;
    const matchesStatus =
      !statusFilter ||
      (statusFilter === "Connected" && isConnected) ||
      (statusFilter === "NotConnected" && !isConnected);

    return matchesSearch && matchesType && matchesStatus;
  });

  const getFieldsForSelectedChannel = (): CredentialField[] => {
    if (!selectedChannel) return [];
    const companyName = selectedChannel.companyName || selectedChannel.title;

    if (partnerFields[companyName]) {
      return partnerFields[companyName];
    }
    if (Array.isArray(selectedChannel.credentialsFields)) {
      return selectedChannel.credentialsFields.map((f: any) => ({
        key: f.key || f.name,
        label: f.label || f.name || f.key,
        required: Boolean(f.required),
        type: f.type || "text",
        placeholder: f.placeholder || "",
      }));
    }
    if (Array.isArray(selectedChannel.credentialKeys)) {
      return selectedChannel.credentialKeys.map((k: any) => ({
        key: typeof k === "string" ? k : k.key,
        label: typeof k === "string" ? k : k.label || k.key,
        required: true,
        type: "text",
        placeholder: `Enter ${typeof k === "string" ? k : k.label || k.key}`,
      }));
    }
    return [
      { key: "accountNumber", label: "Account Number", required: true, placeholder: "Enter Account Number" },
      { key: "apiKey", label: "API Key", required: true, placeholder: "Enter API Key" },
      { key: "apiSecret", label: "API Secret", required: false, type: "password", placeholder: "Leave blank to keep existing" },
    ];
  };

  const isChannelTab =
    activeSection === "All Channels" ||
    activeSection === "Shipping" ||
    activeSection === "Sales";

  const isSidebarOpen = selectedChannel !== null;
  const connectedCount = channelsList.filter((c: any) => c.status === "Connected" || c.isConnected).length;

  const isSelectedTrendyol =
    (selectedChannel?.companyName || selectedChannel?.title)?.toLowerCase() === "trendyol";
  if (isLoading) {
    return null;
  }

  if (!allowedChannels || (Array.isArray(allowedChannels) && allowedChannels.length === 0)) {
    return (
      <div className="p-6 text-center text-gray-500">
        You don't have access to this section
      </div>
    );
  }

  return (
    <div className="px-6 py-4 space-y-3.5">
      {/* Top Header RoleToggle Navigation */}
      <div className="flex items-center justify-between">
        <RoleToggle
          options={[
            "All Channels",
            "Sales",
            "Shipping",
          ]}
          active={activeSection}
          onChange={setActiveSection}
          activeClassName="bg-[#000000] text-[#ffffff] font-bold shadow-sm"
        />

        {/* Top-Right Summary Pill matching Last-Mile-Frontend */}
        <div className="flex items-center gap-2 px-3 py-1 bg-white border border-slate-200/90 rounded-full text-xs font-semibold text-slate-700 shadow-2xs">
          <span>
            {t("total")}: <span className="font-bold text-slate-900">{channelsList.length}</span>
          </span>
          <span className="w-px h-3 bg-slate-200"></span>
          <span>
            {t("connected")}: <span className="font-bold text-emerald-600">{connectedCount}</span>
          </span>
          <span className="w-px h-3 bg-slate-200"></span>
          <span>
            {t("request")}: <span className="font-bold text-amber-600">0</span>
          </span>
        </div>
      </div>

      {/* Main Grid Layout - Split between Channels Grid & Side Panel */}
      <div className="bg-[#FAF9F5]/70 backdrop-blur-xl transition-all duration-300 rounded-2xl w-full relative border border-slate-200/60 p-4 shadow-xs">
        {isChannelTab ? (
          <div className="grid grid-cols-12 gap-5">
            {/* Left Channels Grid Column */}
            <div
              className={`transition-all duration-300 ${isSidebarOpen ? "col-span-7" : "col-span-12"
                }`}
            >
              <div className="bg-white/90 rounded-2xl border border-slate-200/70 p-4 space-y-4">
                {/* Search & Filter bar top right */}
                <div className="flex items-center justify-end gap-2.5">
                  <div
                    onClick={() => setShowSearch(!showSearch)}
                    className={`flex items-center transition-all duration-300 border border-slate-200 rounded-lg bg-white shadow-2xs h-8 ${showSearch ? "w-56 px-2" : "w-8 justify-center cursor-pointer"
                      }`}
                  >
                    <button
                      type="button"
                      className="text-slate-500 hover:text-slate-800 focus:outline-none"
                    >
                      <IconSearch size={16} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <input
                        type="text"
                        placeholder={t("Search channel...")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        className="w-full ml-2 bg-transparent text-xs text-slate-800 outline-none border-none"
                      />
                    )}
                  </div>

                  <div className="relative">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 focus:outline-none shadow-2xs cursor-pointer appearance-none pr-7"
                    >
                      <option value="">{t("All Statuses")}</option>
                      <option value="Connected">{t("Connected")}</option>
                      <option value="NotConnected">{t("Not Connected")}</option>
                    </select>
                    <IconFilter size={14} className="text-slate-400 absolute right-2 top-2 pointer-events-none" />
                  </div>
                </div>

                {/* Cards List Grid matching TrackTech standard */}
                {isLoading ? (
                  <div className="grid 2xl:grid-cols-6 lg:grid-cols-4 md:grid-cols-3 grid-cols-2 gap-4 w-full">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse"></div>
                    ))}
                  </div>
                ) : filteredChannels.length === 0 ? (
                  <div className="text-center py-14 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                    <IconTruckDelivery size={36} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-600 font-semibold text-sm">{t("No Integration Channels Found")}</p>
                    <p className="text-slate-400 text-xs mt-1">
                      {t("Admin has not assigned any active channels matching your search.")}
                    </p>
                  </div>
                ) : (
                  <div
                    className={
                      isSidebarOpen
                        ? "grid 2xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-2 gap-4 w-full"
                        : "grid 2xl:grid-cols-6 lg:grid-cols-5 md:grid-cols-4 grid-cols-3 gap-4 w-full"
                    }
                  >
                    {filteredChannels.map((channel: any) => {
                      const companyName = channel.companyName || channel.title;
                      const isConnected = channel.status === "Connected" || channel.isConnected;
                      const isSelected = selectedChannel?.id === channel.id;

                      return (
                        <div
                          key={channel.id}
                          onClick={() => handleOpenConnect(channel)}
                          className="flex flex-col items-center text-center cursor-pointer group select-none"
                        >
                          {/* Logo Container */}
                          <div
                            className={`w-20 h-20 rounded-2xl flex items-center justify-center border-2 transition-all p-2 bg-white relative ${isSelected
                              ? "border-amber-400 ring-2 ring-amber-200 shadow-md"
                              : isConnected
                                ? "border-emerald-500 shadow-xs ring-2 ring-emerald-100"
                                : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                              }`}
                          >
                            {channel.companyLogo ? (
                              <img
                                src={channel.companyLogo}
                                alt={companyName}
                                className="w-full h-full object-contain p-1"
                              />
                            ) : (
                              <span className="font-extrabold text-slate-700 text-lg">
                                {(companyName || "3P")[0]}
                              </span>
                            )}

                            {isConnected && (
                              <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-xs">
                                <IconCheck size={12} stroke={3} />
                              </div>
                            )}
                          </div>

                          {/* Carrier Name */}
                          <span className="mt-2 font-bold text-xs text-slate-800 truncate max-w-full">
                            {companyName}
                          </span>

                          {/* Status / Connect Button */}
                          <span
                            className={`mt-1.5 text-[10px] font-bold px-3 py-0.5 rounded-full transition-all ${isConnected
                              ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                              : "bg-black text-white hover:bg-slate-800"
                              }`}
                          >
                            {isConnected ? t("Connected") : t("Connect")}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side Panel Column - Redesigned using App Design System */}
            {isSidebarOpen && (
              <div className="col-span-5">
                <div className="sticky top-4 bg-white border border-slate-200/90 rounded-2xl p-4 shadow-sm space-y-3.5 animate-in fade-in slide-in-from-right duration-200">
                  {/* 1. Header Row */}
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      {selectedChannel.companyLogo ? (
                        <img
                          src={selectedChannel.companyLogo}
                          alt={selectedChannel.companyName || selectedChannel.title}
                          className="w-8 h-8 rounded-lg object-contain p-0.5 border border-slate-200/80 bg-white shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-amber-100/70 border border-amber-200/80 flex items-center justify-center font-bold text-slate-800 text-xs shrink-0">
                          {(selectedChannel.companyName || selectedChannel.title || "C")[0]}
                        </div>
                      )}
                      <div className="min-w-0">
                        <h2 className="text-base font-bold text-slate-900 tracking-tight truncate">
                          {selectedChannel.companyName || selectedChannel.title}
                        </h2>
                        <span className="text-[10px] text-slate-400 block font-medium">
                          {selectedChannel.type || selectedChannel.channelType || "Integration"}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {/* Environment Switcher */}
                      <div className="flex items-center bg-slate-100 p-0.5 rounded-full text-[10px] font-bold border border-slate-200/80">
                        <button
                          type="button"
                          onClick={() => setEnvironment("test")}
                          className={`px-2 py-0.5 rounded-full transition-all cursor-pointer ${environment === "test"
                            ? "bg-amber-500 text-white shadow-2xs"
                            : "text-slate-500 hover:text-slate-900"
                            }`}
                        >
                          TEST
                        </button>
                        <button
                          type="button"
                          onClick={() => setEnvironment("prod")}
                          className={`px-2 py-0.5 rounded-full transition-all cursor-pointer ${environment === "prod"
                            ? "bg-emerald-600 text-white shadow-2xs"
                            : "text-slate-500 hover:text-slate-900"
                            }`}
                        >
                          PROD
                        </button>
                      </div>

                      {/* Close Button */}
                      <button
                        type="button"
                        onClick={() => setSelectedChannel(null)}
                        className="p-1 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                        title={t("Close")}
                      >
                        <IconX size={16} />
                      </button>
                    </div>
                  </div>

                  {/* 2. Channel Master Status Banner */}
                  <div className="flex items-center justify-between p-2.5 bg-slate-50/70 border border-slate-200/80 rounded-xl shadow-2xs">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${selectedChannel.status === "Connected" || selectedChannel.isConnected
                          ? "bg-emerald-500 ring-2 ring-emerald-200"
                          : "bg-slate-300"
                          }`}
                      />
                      <div>
                        <span className="text-xs font-bold text-slate-800 block leading-tight">
                          {selectedChannel.status === "Connected" || selectedChannel.isConnected
                            ? t("Store Connected")
                            : t("Store Not Connected")}
                        </span>
                        <span className="text-[11px] text-slate-500 leading-tight">
                          {isSelectedTrendyol
                            ? t("Sync orders and push back tracking with Trendyol")
                            : t("Configure API access credentials")}
                        </span>
                      </div>
                    </div>

                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${selectedChannel.status === "Connected" || selectedChannel.isConnected
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-slate-100 text-slate-500 border border-slate-200"
                        }`}
                    >
                      {selectedChannel.status === "Connected" || selectedChannel.isConnected
                        ? t("Active")
                        : t("Inactive")}
                    </span>
                  </div>

                  {/* 3. Credentials & Settings Form Body */}
                  <form onSubmit={handleSubmit} className="space-y-3 pt-0.5">
                    {isSelectedTrendyol ? (
                      <>
                        {/* Section A: API Access Credentials */}
                        <div>
                          <SectionHeader icon={IconKey} title="API Credentials" />
                          <div className="space-y-2.5">
                            <NewinputField
                              name="sellerId"
                              type="text"
                              title={t("Seller ID")}
                              placeholder="e.g. 123456"
                              value={formData.sellerId || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, sellerId: e.target.value }))
                              }
                              showPasswordStrength={false}
                            />

                            <NewSelectInput
                              name="storefrontCode"
                              label={t("Storefront / Country")}
                              placeholder={t("Select Storefront Country")}
                              selectOptions={STOREFRONT_OPTIONS}
                              value={formData.storefrontCode || "SA"}
                              setValue={(val: string) =>
                                setFormData((prev) => ({ ...prev, storefrontCode: val }))
                              }
                            />

                            <NewinputField
                              name="apiKey"
                              type="password"
                              title={t("API Key")}
                              placeholder={
                                selectedChannel.status === "Connected" || selectedChannel.isConnected
                                  ? t("Leave blank to keep existing key")
                                  : t("Enter Trendyol API Key")
                              }
                              value={formData.apiKey || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, apiKey: e.target.value }))
                              }
                              showPasswordStrength={false}
                            />

                            <NewinputField
                              name="apiSecret"
                              type="password"
                              title={t("API Secret")}
                              placeholder={
                                selectedChannel.status === "Connected" || selectedChannel.isConnected
                                  ? t("Leave blank to keep existing secret")
                                  : t("Enter Trendyol API Secret")
                              }
                              value={formData.apiSecret || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, apiSecret: e.target.value }))
                              }
                              showPasswordStrength={false}
                            />

                            {/* Test Connection Button & Result Alert */}
                            <div className="pt-0.5">
                              <button
                                type="button"
                                onClick={handleTestConnection}
                                disabled={validateTrendyolMutation.isPending}
                                className="w-full flex justify-center items-center gap-2 py-2 px-3 border border-slate-300 hover:border-slate-800 bg-white hover:bg-slate-50 text-slate-800 rounded-lg text-xs font-bold transition-all shadow-2xs cursor-pointer disabled:opacity-50"
                              >
                                {validateTrendyolMutation.isPending ? (
                                  <>
                                    <div className="w-3.5 h-3.5 border-2 border-slate-800 border-t-transparent rounded-full animate-spin" />
                                    <span>{t("Validating with Trendyol...")}</span>
                                  </>
                                ) : (
                                  <>
                                    <IconBolt size={14} className="text-amber-500" />
                                    <span>{t("Test Connection")}</span>
                                  </>
                                )}
                              </button>

                              {testResult && (
                                <div
                                  className={`mt-2 p-2.5 rounded-lg flex items-start gap-2 text-xs border transition-all ${testResult.success
                                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                    : "bg-rose-50 text-rose-800 border-rose-200"
                                    }`}
                                >
                                  {testResult.success ? (
                                    <IconCheck size={15} className="text-emerald-600 mt-0.5 shrink-0" stroke={2.5} />
                                  ) : (
                                    <IconAlertCircle size={15} className="text-rose-600 mt-0.5 shrink-0" />
                                  )}
                                  <div className="flex-1">
                                    <p className="font-bold">
                                      {testResult.success ? t("Connection Verified") : t("Validation Failed")}
                                    </p>
                                    <p className="text-[11px] font-normal opacity-90">{testResult.message}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Section B: Order Routing Defaults */}
                        <div>
                          <SectionHeader icon={IconBuildingWarehouse} title="Routing Defaults" />
                          <div className="grid grid-cols-2 gap-2.5">
                            <NewSelectInput
                              name="warehouseId"
                              label={t("Fulfillment Warehouse")}
                              placeholder={t("Select Warehouse")}
                              selectOptions={warehouseOptions}
                              value={formData.warehouseId || ""}
                              setValue={(val: string) =>
                                setFormData((prev) => ({ ...prev, warehouseId: val }))
                              }
                            />

                            <NewSelectInput
                              name="serviceId"
                              label={t("Default Service")}
                              placeholder={t("Select Service")}
                              selectOptions={serviceOptions}
                              value={formData.serviceId || ""}
                              setValue={(val: string) =>
                                setFormData((prev) => ({ ...prev, serviceId: val }))
                              }
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      /* General Partner Credential Fields using standard app components */
                      <div>
                        <SectionHeader icon={IconKey} title="API Credentials" />
                        <div className="space-y-2.5">
                          {getFieldsForSelectedChannel().map((field) =>
                            field.type === "select" ? (
                              <NewSelectInput
                                key={field.key}
                                name={field.key}
                                label={t(field.label)}
                                placeholder={field.placeholder ? t(field.placeholder) : `${t("Select")} ${t(field.label)}`}
                                selectOptions={
                                  field.options?.map((opt: any) => ({
                                    id: opt.value,
                                    value: opt.value,
                                    text: t(opt.label),
                                  })) || []
                                }
                                value={formData[field.key] || ""}
                                setValue={(val: string) =>
                                  setFormData((prev) => ({ ...prev, [field.key]: val }))
                                }
                              />
                            ) : (
                              <NewinputField
                                key={field.key}
                                name={field.key}
                                type={field.type || "text"}
                                title={t(field.label)}
                                placeholder={
                                  field.placeholder
                                    ? t(field.placeholder)
                                    : `${t("Enter")} ${t(field.label).toLowerCase()}`
                                }
                                value={formData[field.key] || ""}
                                onChange={(e) =>
                                  setFormData((prev) => ({ ...prev, [field.key]: e.target.value }))
                                }
                                showPasswordStrength={false}
                              />
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {/* 4. Bottom Action Footer matching Image 2 */}
                    <div className="pt-2 flex items-center justify-between gap-3 border-t border-slate-200/80">
                      {selectedChannel.status === "Connected" || selectedChannel.isConnected ? (
                        <button
                          type="button"
                          onClick={isSelectedTrendyol ? handleDisconnect : undefined}
                          disabled={disconnectTrendyolMutation.isPending}
                          className="px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-bold text-xs rounded-lg transition-all shadow-2xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                        >
                          <IconTrash size={14} />
                          <span>
                            {disconnectTrendyolMutation.isPending ? t("Disconnecting...") : t("Disconnect Store")}
                          </span>
                        </button>
                      ) : (
                        <div />
                      )}

                      <button
                        type="submit"
                        disabled={connectMutation.isPending || connectTrendyolMutation.isPending}
                        className="px-5 py-2 bg-[#FBD037] hover:bg-yellow-400 text-[#131327] font-extrabold text-xs rounded-lg transition-all shadow-2xs disabled:opacity-50 flex items-center gap-1.5 cursor-pointer ml-auto"
                      >
                        {connectMutation.isPending || connectTrendyolMutation.isPending ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                            <span>{t("Saving...")}</span>
                          </>
                        ) : (
                          <>
                            <IconCheck size={15} stroke={2.5} />
                            <span>
                              {selectedChannel.status === "Connected" || selectedChannel.isConnected
                                ? t("Save Changes")
                                : t("Connect Store")}
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        ) : activeSection === "Payment Gateway" ? (
          <div className="text-center py-16 text-slate-500 font-medium text-sm">
            {t("Payment Gateways configured for your merchant account will be displayed here.")}
          </div>
        ) : (
          <div className="text-center py-16 text-slate-500 font-medium text-sm">
            {t("Communication Hub channels assigned to your account will be displayed here.")}
          </div>
        )}
      </div>
    </div>
  );
};

export default Integration;
